import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../lib/api.js';
import { fmtCT, fmtSheetStamp } from '../lib/time.js';
import { Button } from '../ui/Button.js';
import { Field, Input, Select } from '../ui/Input.js';
import { Badge, Card, CardBody, CardHeader, StatusDot } from '../ui/Card.js';
import { useAuth } from '../lib/auth-context.js';
import {
  PRICING_COMP_CATEGORIES,
  PRICING_COMP_CATEGORY_LABELS,
  type PricingCompCategory,
} from '@mana/shared';

interface StatusEntry {
  configured: boolean;
  lastTestedAt: string | null;
  lastTestStatus: string | null;
  lastTestError: string | null;
}
interface WhatsAppConfigPublic {
  mode: string;
  twilioAccountSid: string | null;
  twilioFromNumber: string | null;
  twilioMessagingServiceSid: string | null;
  twilioDefaultContentSid: string | null;
}
interface WhatsAppStatusEntry extends StatusEntry {
  config: WhatsAppConfigPublic | null;
}
interface UwmPricingConfigPublic {
  environment: string;
  brokerAlias: string | null;
  username: string | null;
}
interface UwmPricingStatusEntry extends StatusEntry {
  config: UwmPricingConfigPublic | null;
}
interface FcmCodaStatusEntry extends StatusEntry {
  config: { username: string; tierKeys: string[] } | null;
}
interface PennymacPowerStatusEntry extends StatusEntry {
  config: { username: string; totpConfigured: boolean } | null;
}
interface SunwestSunsoftConfigPublic {
  username: string;
  companyCode: string;
  signInRefusedAt: string | null;
}
interface SunwestSunsoftStatusEntry extends StatusEntry {
  config: SunwestSunsoftConfigPublic | null;
}
interface PortalLoginConfigPublic {
  username?: string;
  email?: string;
  signInRefusedAt: string | null;
}
interface PortalLoginStatusEntry extends StatusEntry {
  config: PortalLoginConfigPublic | null;
}
/** SendGrid's From identity (the API key never returns). */
interface SendGridConfigPublic {
  fromEmail: string;
  fromName: string;
  replyTo: string | null;
}
interface StatusResponse {
  gmail_smtp: StatusEntry;
  sendgrid?: StatusEntry & { config: SendGridConfigPublic | null };
  whatsapp: WhatsAppStatusEntry;
  uwm: StatusEntry;
  uwm_pricing: UwmPricingStatusEntry;
  fcm_coda?: FcmCodaStatusEntry;
  pennymac_power?: PennymacPowerStatusEntry;
  sunwest_sunsoft?: SunwestSunsoftStatusEntry;
  amwest_pipeline?: PortalLoginStatusEntry;
  springeq_emma?: PortalLoginStatusEntry;
  tls_email?: StatusEntry & { config: { email: string } | null };
  tls_portal?: StatusEntry & { config: { username: string; signInRefusedAt: string | null } | null };
  newrez_portal?: PortalLoginStatusEntry;
  /** Cardinal: the pricing-tier file the daily email import stores (the default until saved; null = the email's file). */
  cardinal_sheet?: StatusEntry & { config: { tier: string | null } | null };
}

export function IntegrationsPage() {
  const { me } = useAuth();
  const isAdmin = me?.user.role === 'admin' || me?.user.role === 'super_admin';
  const [tab, setTab] = useState<'gmail' | 'sendgrid' | 'whatsapp' | 'comp' | 'uwm' | 'uwm_pricing' | 'fcm_coda' | 'pennymac' | 'sunwest' | 'amwest' | 'springeq' | 'tls' | 'newrez' | 'cardinal'>('gmail');

  const statusQuery = useQuery<StatusResponse>({
    queryKey: ['integrations-status'],
    queryFn: () => api('/api/integrations'),
  });

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
          Integrations
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold text-brand-navy-800">
          Connect your providers
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Gmail SMTP, SendGrid and WhatsApp are per-MLO — each connects their own. UWM is org-level (admin only). All secrets are encrypted server-side.
        </p>
      </header>

      <div className="flex gap-2 border-b border-slate-200">
        <TabBtn active={tab === 'gmail'} onClick={() => setTab('gmail')}>
          Gmail SMTP
          <StatusPill entry={statusQuery.data?.gmail_smtp} />
        </TabBtn>
        <TabBtn active={tab === 'sendgrid'} onClick={() => setTab('sendgrid')}>
          SendGrid
          <StatusPill entry={statusQuery.data?.sendgrid} />
        </TabBtn>
        <TabBtn active={tab === 'whatsapp'} onClick={() => setTab('whatsapp')}>
          WhatsApp
          <StatusPill entry={statusQuery.data?.whatsapp} />
        </TabBtn>
        <TabBtn active={tab === 'comp'} onClick={() => setTab('comp')}>
          Set Comp and Closing Costs
        </TabBtn>
        {/* UWM Insights + Pricing are org-level config — admins only. MLOs still
            use this page for their per-MLO Gmail SMTP, so we hide just these two
            tabs (their config endpoints are also requireAdminRole on the API). */}
        {isAdmin && (
          <TabBtn active={tab === 'uwm'} onClick={() => setTab('uwm')}>
            UWM Insights
            <StatusPill entry={statusQuery.data?.uwm} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'uwm_pricing'} onClick={() => setTab('uwm_pricing')}>
            UWM Pricing
            <StatusPill entry={statusQuery.data?.uwm_pricing} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'fcm_coda'} onClick={() => setTab('fcm_coda')}>
            First Colony (CODA)
            <StatusPill entry={statusQuery.data?.fcm_coda} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'pennymac'} onClick={() => setTab('pennymac')}>
            PennyMac rate sheet
            <StatusPill entry={statusQuery.data?.pennymac_power} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'sunwest'} onClick={() => setTab('sunwest')}>
            Sun West rate sheet
            <StatusPill entry={statusQuery.data?.sunwest_sunsoft} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'amwest'} onClick={() => setTab('amwest')}>
            AmWest rate sheet
            <StatusPill entry={statusQuery.data?.amwest_pipeline} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'springeq'} onClick={() => setTab('springeq')}>
            Spring EQ rate sheet
            <StatusPill entry={statusQuery.data?.springeq_emma} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'tls'} onClick={() => setTab('tls')}>
            Averra Financial rate sheet
            <StatusPill entry={statusQuery.data?.tls_email} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'newrez'} onClick={() => setTab('newrez')}>
            Newrez rate sheet
            <StatusPill entry={statusQuery.data?.newrez_portal} />
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === 'cardinal'} onClick={() => setTab('cardinal')}>
            Cardinal rate sheet
          </TabBtn>
        )}
      </div>

      {tab === 'gmail' && <GmailPanel />}
      {tab === 'sendgrid' && <SendGridPanel existing={statusQuery.data?.sendgrid?.config ?? null} />}
      {tab === 'comp' && <CompCostsPanel />}
      {tab === 'whatsapp' && (
        <WhatsAppPanel
          canEdit={true}
          existingConfig={statusQuery.data?.whatsapp.config ?? null}
        />
      )}
      {isAdmin && tab === 'uwm' && <UwmPanel canEdit={isAdmin} />}
      {isAdmin && tab === 'uwm_pricing' && (
        <UwmPricingPanel canEdit={isAdmin} existingConfig={statusQuery.data?.uwm_pricing.config ?? null} />
      )}
      {isAdmin && tab === 'fcm_coda' && (
        <FcmCodaPanel canEdit={isAdmin} existingConfig={statusQuery.data?.fcm_coda?.config ?? null} />
      )}
      {isAdmin && tab === 'pennymac' && (
        <PennymacPanel canEdit={isAdmin} existingConfig={statusQuery.data?.pennymac_power?.config ?? null} />
      )}
      {isAdmin && tab === 'sunwest' && (
        <SunwestPanel canEdit={isAdmin} existingConfig={statusQuery.data?.sunwest_sunsoft?.config ?? null} />
      )}
      {isAdmin && tab === 'amwest' && (
        <PortalPdfSheetPanel
          canEdit={isAdmin}
          existingConfig={statusQuery.data?.amwest_pipeline?.config ?? null}
          lenderKey="amwest"
          providerPath="amwest-pipeline"
          loginKind="username"
          title="AmWest Funding — wholesale rate sheet"
          subtitle="Save the AmWest broker-portal login (pipeline.amwestwholesale.com) and the worker signs in every hour and downloads the newest wholesale rate sheet from Rate Sheets, keeping a new version only when the pricing changed (midday price changes included). The Other Lenders and Mana Pricing modules price Conventional (conforming, high balance, Fast Track, HomeReady, Home Possible, HomeOne, RefiNow / Refi Possible) on the newest sheet. Manual upload of the PDF stays available below."
          loginLabel="Portal user name"
          portalName="AmWest's broker portal"
          fileHint="Choose the Wholesale Rate Sheet PDF first."
          noSheetText="No AmWest sheet stored yet."
          engineName="AmWest’s Price Engine"
        />
      )}
      {isAdmin && tab === 'springeq' && (
        <PortalPdfSheetPanel
          canEdit={isAdmin}
          existingConfig={statusQuery.data?.springeq_emma?.config ?? null}
          lenderKey="springeq"
          providerPath="springeq-emma"
          loginKind="email"
          title="Spring EQ — wholesale home-equity rate sheet"
          subtitle="Save the EMMA login (emma.springeq.com) and the worker signs in every hour and downloads the newest home-equity rate sheet from RateSheets, keeping a new version only when the pricing changed. HELOC and second-lien scenarios price Spring EQ's fixed-rate home equity loan, variable HELOC and fixed-rate HELOC on the newest sheet, next to UWM's HELOC. Manual upload of the PDF stays available below."
          loginLabel="EMMA email"
          portalName="EMMA"
          fileHint="Choose the Wholesale Home Equity Rate Sheets PDF first."
          noSheetText="No Spring EQ sheet stored yet."
        />
      )}
      {isAdmin && tab === 'tls' && <TlsSheetPanel canEdit={isAdmin} existingEmail={statusQuery.data?.tls_email?.config?.email ?? null} existingPortalUser={statusQuery.data?.tls_portal?.config?.username ?? null} />}
      {isAdmin && tab === 'cardinal' && (
        <CardinalSheetPanel
          canEdit={isAdmin}
          mailboxEmail={statusQuery.data?.tls_email?.config?.email ?? null}
          tier={statusQuery.data?.cardinal_sheet ? (statusQuery.data.cardinal_sheet.config?.tier ?? null) : undefined}
          tierConfigured={!!statusQuery.data?.cardinal_sheet?.configured}
        />
      )}
      {isAdmin && tab === 'newrez' && (
        <PortalPdfSheetPanel
          canEdit={isAdmin}
          existingConfig={statusQuery.data?.newrez_portal?.config ?? null}
          lenderKey="newrez"
          providerPath="newrez-portal"
          loginKind="username"
          title="Newrez — wholesale rate sheet"
          subtitle="Save the Loan Advisor login (pricing.newrez.com — Okta user name and password, no one-time code) and the worker signs in every hour in a headless browser, searches today's publications under View Ratesheets and downloads the newest Wholesale workbook that has a file, keeping a new version only when the pricing changed. Mana Pricing prices Conventional (conforming, high balance, SOFR ARMs) and FHA / VA / USDA on the newest sheet; Newrez's jumbo, Non-QM and second-lien programs are not priced yet. Manual upload of the .xlsx stays available below."
          loginLabel="Loan Advisor user name"
          portalName="Newrez Loan Advisor"
          fileHint="Choose the Newrez WHL workbook (.xlsx) first."
          noSheetText="No Newrez sheet stored yet."
          fileAccept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />
      )}
    </div>
  );
}

// The Loan Store — TLS emails its rate-sheet workbook ("TLS <date> - Price Update") every time it
// reprices. With the mailbox's Google app password saved, the worker reads the mailbox every 5
// minutes (read-only IMAP) and imports each new version; manual upload of the .xls stays available.
function TlsSheetPanel({ canEdit, existingEmail, existingPortalUser }: { canEdit: boolean; existingEmail: string | null; existingPortalUser: string | null }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [mailMsg, setMailMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [portalUser, setPortalUser] = useState('');
  const [portalPass, setPortalPass] = useState('');
  const [portalMsg, setPortalMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (existingEmail) setEmail(existingEmail);
  }, [existingEmail]);
  useEffect(() => {
    if (existingPortalUser) setPortalUser(existingPortalUser);
  }, [existingPortalUser]);
  const savePortal = useMutation({
    mutationFn: () => {
      if (!portalUser.trim() || !portalPass) throw new Error('Enter the TPO Connect user name and password.');
      return api<{ ok: boolean }>('/api/integrations/tls-portal', { method: 'POST', body: JSON.stringify({ username: portalUser.trim(), password: portalPass }) });
    },
    onSuccess: () => {
      setPortalMsg({ tone: 'ok', text: 'Saved. The password is stored encrypted and never shown again. The nightly audit will use it to price on TLS’s portal and confirm Mana matches.' });
      setPortalPass('');
      void qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setPortalMsg({ tone: 'err', text: e.message }),
  });
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const lender = status.data?.lenders.find((l) => l.key === 'tls');
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
    void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
    void qc.invalidateQueries({ queryKey: ['integrations-status'] });
  };
  const save = useMutation({
    mutationFn: () => {
      if (!email.trim() || !appPassword.trim()) throw new Error('Enter the mailbox address and its Google app password.');
      return api<{ ok: boolean }>('/api/integrations/tls-email', { method: 'POST', body: JSON.stringify({ email: email.trim(), appPassword }) });
    },
    onSuccess: () => {
      setMailMsg({ tone: 'ok', text: 'Saved. The app password is stored encrypted and never shown again. Click “Check mailbox now” to test it — the worker checks every 5 minutes from now on.' });
      setAppPassword('');
      refreshAll();
    },
    onError: (e: Error) => setMailMsg({ tone: 'err', text: e.message }),
  });
  const check = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; checked: number; imported: string[]; latest: { subject: string; receivedAt: string; fileName: string | null } | null } | null }>(
        '/api/integrations/tls-email/test',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      const d = r.detail;
      setMailMsg(
        r.ok && d
          ? {
              tone: 'ok',
              text:
                d.status === 'downloaded'
                  ? `Signed in — imported ${d.imported.length} new sheet version${d.imported.length === 1 ? '' : 's'} (${d.imported.join(', ')}).`
                  : d.status === 'unchanged'
                    ? `Signed in — ${d.checked} TLS email${d.checked === 1 ? '' : 's'} in the last 3 days, all already stored${d.latest ? ` (newest: “${d.latest.subject}”, ${fmtCT(d.latest.receivedAt)})` : ''}.`
                    : 'Signed in — no TLS rate-sheet email in the last 3 days.',
            }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      refreshAll();
    },
    onError: (e: Error) => setMailMsg({ tone: 'err', text: e.message }),
  });
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose the TLS rate-sheet workbook (.xls) first.');
      return api<{ ok: boolean; versionKey: string; sheetDateTime: string | null; gridCount: number; convGrids: number; isCurrent: boolean; replacedExisting: boolean }>(
        `/api/lender-sheets/upload?lender=tls&fileName=${encodeURIComponent(file.name)}`,
        { method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' } },
      );
    },
    onSuccess: (r) => {
      setMsg({
        tone: r.convGrids > 0 ? 'ok' : 'err',
        text:
          r.convGrids > 0
            ? `Stored the ${r.sheetDateTime ?? r.versionKey} sheet — ${r.gridCount} rate grids (${r.convGrids} conventional)${r.isCurrent ? ' · now current' : ' · a newer sheet stays current'}${r.replacedExisting ? ' · replaced the same version' : ''}.`
            : `Stored the ${r.sheetDateTime ?? r.versionKey} sheet but found no conventional rate grids in it. Check that this is TLS's rate-sheet workbook.`,
      });
      setFile(null);
      refreshAll();
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const runs = (lender?.runs ?? []).slice(0, 6);
  return (
    <Card>
      <CardHeader
        title="Averra Financial (formerly The Loan Store) — wholesale rate sheet"
        subtitle="Averra emails its rate-sheet workbook (“Averra <date> - Price Update”, still sent from tls.rates@theloanstore.com; @averrafi.com senders are read too) every time it reprices. Save the Google app password of the mailbox those emails go to and the worker reads it every 5 minutes — read-only: nothing is marked, moved or deleted — and prices Averra on the newest version. Mana Pricing and Other Lenders price Averra Conventional (conforming, high balance, HomeReady, Home Possible, ARMs, seller-paid buydowns) and its DSCR investor programs from it."
        actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>}
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Mailbox (Gmail address)">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} disabled={!canEdit} autoComplete="off" type="email" placeholder="getloansiftercode@gmail.com" />
          </Field>
          <Field label="Google app password" hint="Google Account → Security → 2-Step Verification → App passwords. Stored encrypted server-side; never returned to the browser.">
            <Input type="password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} disabled={!canEdit} autoComplete="new-password" placeholder="16 letters" />
          </Field>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={!canEdit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save mailbox'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || check.isPending || !existingEmail} onClick={() => check.mutate()} title="Signs in to the mailbox and imports any TLS rate-sheet email not stored yet (a few seconds)">
            {check.isPending ? 'Checking the mailbox…' : 'Check mailbox now'}
          </Button>
        </div>
        {mailMsg && <ResultLine tone={mailMsg.tone} text={mailMsg.text} />}
        {runs.length > 0 && (
          <div className="mt-5">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Recent mailbox checks</p>
            <ul className="space-y-1 text-xs">
              {runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-baseline gap-2">
                  <span className="tabular-nums text-slate-500">{fmtCT(r.startedAt)}</span>
                  <span className="text-slate-400">{r.trigger === 'cron' ? 'automatic' : 'manual'}</span>
                  {r.status === 'ok' ? <Badge tone="success">{r.downloaded ? 'new sheet' : 'no change'}</Badge> : r.status === 'error' ? <Badge tone="danger">failed</Badge> : <Badge tone="neutral">running</Badge>}
                  {r.message && <span className="text-slate-600">{r.message}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-sm font-semibold text-brand-navy-800">Portal login — for the nightly portal audit</p>
          <p className="mt-0.5 text-xs text-slate-500">
            The nightly audit prices the test scenarios on TLS’s own portal (TPO Connect / Polly) and confirms Mana’s prices match. Save the TPO Connect sign-in
            (averrafi.encompasstpoconnect.com since the lender became Averra Financial on Oct 1, 2026 — the same login you use in the browser). Read-only: it prices scenarios only, never registers or locks a loan.
            If TLS’s sign-in asks for a one-time code, the audit can’t answer it — I’ll let you know if that comes up.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="TPO Connect user name">
              <Input value={portalUser} onChange={(e) => setPortalUser(e.target.value)} disabled={!canEdit} autoComplete="off" type="text" />
            </Field>
            <Field label="Password" hint="Stored encrypted server-side. Never returned to the browser — re-enter to change.">
              <Input type="password" value={portalPass} onChange={(e) => setPortalPass(e.target.value)} disabled={!canEdit} autoComplete="new-password" />
            </Field>
          </div>
          <div className="mt-3">
            <Button disabled={!canEdit || savePortal.isPending} onClick={() => savePortal.mutate()}>
              {savePortal.isPending ? 'Saving…' : 'Save portal login'}
            </Button>
          </div>
          {portalMsg && <ResultLine tone={portalMsg.tone} text={portalMsg.text} />}
        </div>

        <p className="mb-1.5 mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Manual upload</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel"
            disabled={!canEdit}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-navy-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-navy-700 hover:file:bg-brand-navy-100"
          />
          <Button disabled={!canEdit || !file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : 'Upload rate sheet'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
        <LenderCompControl lenderKey="tls" canEdit={canEdit} />
        <div className="mt-5">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Stored sheets</p>
          {!lender || lender.versions.length === 0 ? (
            <p className="text-sm text-slate-500">No TLS sheet stored yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1 pr-3">Sheet</th>
                  <th className="py-1 pr-3">File</th>
                  <th className="py-1 pr-3">Rate grids</th>
                  <th className="py-1 pr-3">Stored</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {lender.versions.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-800">{v.sheetDateTime ?? v.versionKey}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.sourceFileName}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.productCount}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{fmtCT(v.fetchedAt)}</td>
                    <td className="py-1.5">{v.isCurrent && <Badge tone="info">current</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// Cardinal Financial — the wholesale rate sheet (CF_WL_<tier>.xls). Cardinal's daily "Today's
// Rates" email (forwarded to the rate-sheet mailbox saved under Averra) links the day's workbook
// on Cardinal's public CDN; the worker follows it every 5 minutes and stores the configured
// pricing tier's file. Manual upload of a workbook stays available.
function CardinalSheetPanel({ canEdit, mailboxEmail, tier, tierConfigured }: { canEdit: boolean; mailboxEmail: string | null; tier: string | null | undefined; tierConfigured: boolean }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [tierInput, setTierInput] = useState('');
  const [mailMsg, setMailMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (tier !== undefined) setTierInput(tier ?? '');
  }, [tier]);
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const lender = status.data?.lenders.find((l) => l.key === 'cardinal');
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
    void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
    void qc.invalidateQueries({ queryKey: ['integrations-status'] });
  };
  const saveTier = useMutation({
    mutationFn: () => api<{ ok: boolean; tier: string | null }>('/api/integrations/cardinal-sheet', { method: 'POST', body: JSON.stringify({ tier: tierInput }) }),
    onSuccess: (r) => {
      setMailMsg({ tone: 'ok', text: r.tier ? `Saved — the daily import stores CF_WL_${r.tier}.xls (the email's link is followed to the same folder).` : 'Saved — the daily import stores exactly the file the email links.' });
      refreshAll();
    },
    onError: (e: Error) => setMailMsg({ tone: 'err', text: e.message }),
  });
  const check = useMutation({
    mutationFn: () =>
      api<{
        ok: boolean;
        errorMessage?: string | null;
        detail?: { status: string; checked: number; imported: string[]; latest: { subject: string; receivedAt: string; linkTier: string | null } | null; tier: string | null; cdn: { status: string; note: string | null } | null } | null;
      }>('/api/integrations/cardinal-email/test', { method: 'POST', body: '{}' }),
    onSuccess: (r) => {
      const d = r.detail;
      const newest = d?.latest ? ` (newest: “${d.latest.subject}”, ${fmtCT(d.latest.receivedAt)}${d.latest.linkTier ? `, links CF_WL_${d.latest.linkTier}` : ''})` : '';
      const cdn = d?.cdn ? ` The CDN file is ${d.cdn.status}${d.cdn.note ? ` (${d.cdn.note})` : ''}.` : '';
      setMailMsg(
        r.ok && d
          ? {
              tone: 'ok',
              text:
                d.status === 'downloaded'
                  ? `Mailbox read — stored ${d.imported.length} new sheet version${d.imported.length === 1 ? '' : 's'}: ${d.imported.join(', ')}.`
                  : d.status === 'unchanged'
                    ? `Mailbox read — ${d.checked} Cardinal email${d.checked === 1 ? '' : 's'} in the last 2 days, the sheet is already stored${newest}.${cdn}`
                    : `Mailbox read — no Cardinal “Today's Rates” email in the last 2 days.${cdn}`,
            }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      refreshAll();
    },
    onError: (e: Error) => setMailMsg({ tone: 'err', text: e.message }),
  });
  const runs = (lender?.runs ?? []).slice(0, 6);
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose the Cardinal rate-sheet workbook (CF_WL_….xls) first.');
      return api<{ ok: boolean; versionKey: string; sheetDateTime: string | null; version: string | null; gridCount: number; convGrids: number; govGrids: number; isCurrent: boolean; replacedExisting: boolean }>(
        `/api/lender-sheets/upload?lender=cardinal&fileName=${encodeURIComponent(file.name)}`,
        { method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' } },
      );
    },
    onSuccess: (r) => {
      setMsg({
        tone: r.convGrids > 0 ? 'ok' : 'err',
        text:
          r.convGrids > 0
            ? `Stored the ${r.sheetDateTime ?? r.versionKey} sheet (version ${r.version ?? '?'}) — ${r.gridCount} rate grids (${r.convGrids} conventional, ${r.govGrids} government)${r.isCurrent ? ' · now current' : ' · a newer sheet stays current'}${r.replacedExisting ? ' · replaced the same version' : ''}.`
            : `Stored the ${r.sheetDateTime ?? r.versionKey} sheet but found no conventional rate grids in it. Check that this is Cardinal's CF_WL workbook.`,
      });
      setFile(null);
      refreshAll();
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  return (
    <Card>
      <CardHeader
        title="Cardinal Financial — wholesale rate sheet"
        subtitle="Cardinal's wholesale rate sheet (the CF_WL_<tier>.xls workbook) prices Conventional (FNMA, FHLMC, Non-Agency second home / investment, high balance, SOFR ARMs) and FHA / VA / USDA in Mana Pricing. Cardinal's daily “Today's Rates” email links the day's workbook; the worker follows that link every 5 minutes and stores each new version on its own. A workbook can also be uploaded here. Jumbo and Non-QM (DSCR) are on Cardinal's other sheets, not loaded yet."
        actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>}
      />
      <CardBody>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Upload</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel"
            disabled={!canEdit}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-navy-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-navy-700 hover:file:bg-brand-navy-100"
          />
          <Button disabled={!canEdit || !file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : 'Upload rate sheet'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
        <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <p className="text-sm font-semibold text-brand-navy-800">Daily email import</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Cardinal's account executive emails “Today's Rates” every morning; its “View Rates” button links the day's workbook in Cardinal's public rate-sheet folder (no sign-in). The email is
            forwarded to the rate-sheet mailbox saved under Averra Financial
            {mailboxEmail ? (
              <>
                {' '}
                (<span className="font-medium text-slate-700">{mailboxEmail}</span>)
              </>
            ) : (
              <>
                {' '}
                — <span className="font-medium text-amber-700">no mailbox saved yet</span>
              </>
            )}
            . The worker reads it every 5 minutes (read-only: nothing is marked, moved or deleted) and stores each new version; between emails it also checks the folder's file itself, so the
            sheet lands even when the forward is late.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Field
              label="Mana's pricing tier (the number in the file name)"
              hint="Cardinal publishes the same sheet as ten pricing tiers (CF_WL_810021 … CF_WL_837021, prices about 0.35 pt apart) and the email links one of them. CF_WL_828021 matched Octane's ad hoc quotes on Oct 16, 2026, so that file is stored whatever the email links. Blank = store exactly the file the email links."
            >
              <Input value={tierInput} onChange={(e) => setTierInput(e.target.value)} disabled={!canEdit} placeholder="828021" className="w-44" />
            </Field>
            <Button disabled={!canEdit || saveTier.isPending} onClick={() => saveTier.mutate()}>
              {saveTier.isPending ? 'Saving…' : 'Save tier'}
            </Button>
            <Button variant="secondary" disabled={!canEdit || check.isPending || !mailboxEmail} onClick={() => check.mutate()} title="Reads the mailbox and stores today's Cardinal sheet if it is not stored yet (a few seconds)">
              {check.isPending ? 'Checking the mailbox…' : 'Check mailbox now'}
            </Button>
          </div>
          {!tierConfigured && tier && <p className="mt-2 text-xs text-slate-500">Using the Octane-matched tier CF_WL_{tier}.xls until a tier is saved.</p>}
          {mailMsg && <ResultLine tone={mailMsg.tone} text={mailMsg.text} />}
          {runs.length > 0 && (
            <div className="mt-4">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Recent mailbox checks</p>
              <ul className="space-y-1 text-xs">
                {runs.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-baseline gap-2">
                    <span className="tabular-nums text-slate-500">{fmtCT(r.startedAt)}</span>
                    <span className="text-slate-400">{r.trigger === 'cron' ? 'automatic' : 'manual'}</span>
                    {r.status === 'ok' ? <Badge tone="success">{r.downloaded ? 'new sheet' : 'no change'}</Badge> : r.status === 'error' ? <Badge tone="danger">failed</Badge> : <Badge tone="neutral">running</Badge>}
                    {r.message && <span className="text-slate-600">{r.message}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <LenderCompControl lenderKey="cardinal" canEdit={canEdit} />
        <div className="mt-5">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Stored sheets</p>
          {!lender || lender.versions.length === 0 ? (
            <p className="text-sm text-slate-500">No Cardinal sheet stored yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1 pr-3">Sheet</th>
                  <th className="py-1 pr-3">File</th>
                  <th className="py-1 pr-3">Rate grids</th>
                  <th className="py-1 pr-3">Stored</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {lender.versions.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-800">{v.sheetDateTime ?? v.versionKey}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.sourceFileName}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.productCount}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{fmtCT(v.fetchedAt)}</td>
                    <td className="py-1.5">{v.isCurrent && <Badge tone="info">current</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// AmWest / Spring EQ — a saved portal login makes the worker download the rate-sheet PDF every
// hour (plain HTTP sign-in, no browser); manual upload of the PDF stays available. Two failed
// sign-ins in a row pause the pull until the login is re-saved or tested.
function PortalPdfSheetPanel({
  canEdit,
  existingConfig,
  lenderKey,
  providerPath,
  loginKind,
  title,
  subtitle,
  loginLabel,
  portalName,
  fileHint,
  noSheetText,
  engineName,
  fileAccept,
}: {
  canEdit: boolean;
  existingConfig: PortalLoginConfigPublic | null;
  lenderKey: 'amwest' | 'springeq' | 'newrez';
  providerPath: 'amwest-pipeline' | 'springeq-emma' | 'newrez-portal';
  loginKind: 'username' | 'email';
  title: string;
  subtitle: string;
  loginLabel: string;
  portalName: string;
  fileHint: string;
  noSheetText: string;
  /** When set, the panel shows the rate-stack extension (the lender's own pricing engine) with a "pull now" button. */
  engineName?: string;
  /** The upload's accepted file types (default: PDF). */
  fileAccept?: string;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [loginMsg, setLoginMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (!existingConfig) return;
    setLogin((loginKind === 'email' ? existingConfig.email : existingConfig.username) ?? '');
  }, [existingConfig, loginKind]);
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const lender = status.data?.lenders.find((l) => l.key === lenderKey);
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
    void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
    void qc.invalidateQueries({ queryKey: ['integrations-status'] });
  };
  const saveLogin = useMutation({
    mutationFn: () => {
      if (!login.trim() || !password) throw new Error(`Enter the ${loginLabel.toLowerCase()} and password.`);
      return api<{ ok: boolean }>(`/api/integrations/${providerPath}`, {
        method: 'POST',
        body: JSON.stringify(loginKind === 'email' ? { email: login.trim(), password } : { username: login.trim(), password }),
      });
    },
    onSuccess: () => {
      setLoginMsg({ tone: 'ok', text: 'Saved. The password is stored encrypted and never shown again. Click “Test sign-in & pull now” to check it — the worker downloads the sheet every hour from now on.' });
      setPassword('');
      refreshAll();
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  const testPull = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; sheetDateTime?: string | null; sheetDate?: string | null; label?: string; listingName?: string; versionKey: string; productCount?: number; convGrids?: number; products?: string[]; isCurrent?: boolean } | null }>(
        `/api/integrations/${providerPath}/test`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      const d = r.detail;
      const when = d?.sheetDateTime ?? d?.sheetDate ?? 'undated';
      const what = d?.productCount != null ? `${d.productCount} rate grids${d.convGrids != null ? ` (${d.convGrids} conventional)` : ''}` : d?.products?.length ? d.products.join(', ') : 'stored';
      setLoginMsg(
        r.ok
          ? {
              tone: 'ok',
              text:
                d?.status === 'downloaded'
                  ? `Signed in and pulled the ${when} sheet${d.label || d.listingName ? ` (${d.label ?? d.listingName})` : ''} — ${what}${d.isCurrent ? ' · now current' : ' · a newer sheet stays current'}.`
                  : `Signed in — ${portalName}'s current sheet (${when}) is already stored.`,
            }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      refreshAll();
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error(fileHint);
      return api<{ ok: boolean; sheetDate?: string | null; sheetTime?: string | null; sheetDateTime?: string | null; productCount: number; convGrids?: number; convProducts?: number; products?: string[]; isCurrent: boolean; replacedExisting: boolean; notes: string[] }>(
        `/api/lender-sheets/upload?lender=${lenderKey}&fileName=${encodeURIComponent(file.name)}`,
        { method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' } },
      );
    },
    onSuccess: (r) => {
      // Newrez stamps its sheets in Central time (sheetDateTime); the PDF lenders print Pacific date + time.
      const when = r.sheetDateTime ?? (r.sheetDate ? `${r.sheetDate}${r.sheetTime ? ` ${r.sheetTime} PT` : ''}` : 'undated');
      const conv = r.convGrids ?? r.convProducts;
      const bad = lenderKey === 'amwest' || lenderKey === 'newrez' ? (conv ?? 0) === 0 : r.productCount === 0;
      setMsg({
        tone: bad ? 'err' : 'ok',
        text: bad
          ? `Stored the ${when} sheet but found no usable rate grids in it (${r.productCount} grids read). Check that this is the right file.`
          : `Stored the ${when} sheet — ${r.productCount} rate grids${conv != null ? ` (${conv} conventional)` : ''}${r.products?.length ? ` (${r.products.join(', ')})` : ''}${r.isCurrent ? ' · now current' : ' · a newer sheet stays current'}${r.replacedExisting ? ' · replaced the same version' : ''}.`,
      });
      setFile(null);
      refreshAll();
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const runs = (lender?.runs ?? []).slice(0, 5);
  // The rates the lender's own pricing engine offers beyond the printed sheet (built once per sheet version with the saved login).
  const current = lender?.current[0] ?? null;
  const ext = current?.extension ?? null;
  const extend = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; versionKey?: string; added?: number; gridsExtended?: number; gridsSkipped?: number; scenarios?: number; notes?: string[] } }>(
        `/api/integrations/${providerPath}/extend`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      const d = r.detail;
      setLoginMsg(
        r.ok && d
          ? { tone: 'ok', text: `${engineName ?? 'The engine'} priced ${d.scenarios ?? '?'} scenarios — ${d.added ?? 0} extra rate${d.added === 1 ? '' : 's'} on ${d.gridsExtended ?? 0} grids${d.gridsSkipped ? ` (${d.gridsSkipped} grids skipped: their printed rates did not match the engine)` : ''}. They price in Mana Pricing and Other Lenders right away.` }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      refreshAll();
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>} />
      <CardBody>
        {existingConfig?.signInRefusedAt && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Hourly pull paused — {portalName} refused the saved login twice in a row (last on {fmtCT(existingConfig.signInRefusedAt)}). Re-save the login, or run the test, to resume. Pausing keeps repeated failed sign-ins from locking the account.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label={loginLabel}>
            <Input value={login} onChange={(e) => setLogin(e.target.value)} disabled={!canEdit} autoComplete="off" type={loginKind === 'email' ? 'email' : 'text'} />
          </Field>
          <Field label="Password" hint="Stored encrypted server-side. Never returned to the browser — re-enter to change.">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={!canEdit} autoComplete="new-password" />
          </Field>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={!canEdit || saveLogin.isPending} onClick={() => saveLogin.mutate()}>
            {saveLogin.isPending ? 'Saving…' : 'Save login'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || testPull.isPending || !existingConfig} onClick={() => testPull.mutate()} title="Signs in and downloads the rate sheet now (a few seconds)">
            {testPull.isPending ? 'Signing in and downloading…' : 'Test sign-in & pull now'}
          </Button>
          {engineName && (
            <Button variant="secondary" disabled={!canEdit || extend.isPending || !existingConfig || !current} onClick={() => extend.mutate()} title={`Signs in and prices every conventional grid on ${engineName} (about a minute); stores the rates its ladders carry beyond the printed sheet`}>
              {extend.isPending ? `Pricing on ${engineName}…` : `Pull the extra rates from ${engineName} now`}
            </Button>
          )}
        </div>
        {loginMsg && <ResultLine tone={loginMsg.tone} text={loginMsg.text} />}
        {engineName && (
          <p className="mt-3 text-xs text-slate-600">
            <span className="font-semibold uppercase tracking-wide text-slate-500">Extra rates from {engineName}: </span>
            {!current
              ? 'no sheet loaded yet.'
              : ext
                ? `sheet ${fmtSheetStamp(current)} · ${ext.added} rate${ext.added === 1 ? '' : 's'} beyond the printed sheet on ${ext.gridsExtended} grids${ext.gridsSkipped ? ` · ${ext.gridsSkipped} grids skipped (printed rates did not match the engine)` : ''} · pulled ${fmtCT(ext.fetchedAt)}`
                : existingConfig
                  ? `not built yet for sheet ${fmtSheetStamp(current)} — it runs with the next hourly pull, or click the button.`
                  : 'save the portal login first — the hourly pull then also prices the grids on the engine.'}
          </p>
        )}
        {engineName && ext && ext.notes.length > 0 && (
          <details className="mt-1 text-[11px] text-slate-500">
            <summary className="cursor-pointer">Notes ({ext.notes.length})</summary>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {ext.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </details>
        )}
        {runs.length > 0 && (
          <div className="mt-5">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Recent pulls</p>
            <ul className="space-y-1 text-xs">
              {runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-baseline gap-2">
                  <span className="tabular-nums text-slate-500">{fmtCT(r.startedAt)}</span>
                  <span className="text-slate-400">{r.trigger === 'cron' ? 'hourly' : 'manual'}</span>
                  {r.status === 'ok' ? <Badge tone="success">{r.downloaded ? 'new sheet' : 'no change'}</Badge> : r.status === 'error' ? <Badge tone="danger">failed</Badge> : <Badge tone="neutral">running</Badge>}
                  {r.message && <span className="text-slate-600">{r.message}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mb-1.5 mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Manual upload</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept={fileAccept ?? '.pdf,application/pdf'}
            disabled={!canEdit}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-navy-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-navy-700 hover:file:bg-brand-navy-100"
          />
          <Button disabled={!canEdit || !file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : 'Upload rate sheet'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
        <LenderCompControl lenderKey={lenderKey} canEdit={canEdit} />
        <div className="mt-5">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Stored sheets</p>
          {!lender || lender.versions.length === 0 ? (
            <p className="text-sm text-slate-500">{noSheetText}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1 pr-3">Sheet date</th>
                  <th className="py-1 pr-3">File</th>
                  <th className="py-1 pr-3">Rate grids</th>
                  <th className="py-1 pr-3">Stored</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {lender.versions.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-800">{v.sheetDateTime ?? v.versionKey}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.sourceFileName}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.productCount}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{fmtCT(v.fetchedAt)}</td>
                    <td className="py-1.5">{v.isCurrent && <Badge tone="info">current</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

interface LenderSheetVersion {
  id: string;
  family?: string;
  versionKey: string;
  sheetDateTime: string | null;
  /** The sheet's own publish instant (ISO), when its cover carries one. */
  sheetAt?: string | null;
  sourceFileName: string;
  productCount: number;
  isCurrent: boolean;
  fetchedAt: string;
  hasPrices: boolean | null;
  /** Extra rates pulled from the lender's own pricing engine (First Colony: CODA), when built. */
  extension?: { source: string; fetchedAt: string; added: number; gridsExtended: number; gridsSkipped: number; notes: string[] } | null;
}
interface LenderSheetRun {
  id: string;
  trigger: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  downloaded: number;
  message: string | null;
}
interface LenderSheetStatus {
  lenders: Array<{ key: string; name: string; source: string; lpCompPct: number; current: LenderSheetVersion[]; versions: LenderSheetVersion[]; runs: LenderSheetRun[] }>;
}

// Per-lender lender-paid comp percent (admin). An "LP" scenario in Other Lenders
// Pricing shows every price net of this; "BP" keeps the gross sheet price.
function LenderCompControl({ lenderKey, canEdit }: { lenderKey: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const current = status.data?.lenders.find((l) => l.key === lenderKey)?.lpCompPct;
  const [pct, setPct] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (current != null) setPct(current.toFixed(3));
  }, [current]);
  const save = useMutation({
    mutationFn: () => {
      const n = Number(pct);
      if (!Number.isFinite(n) || n < 0 || n > 5) throw new Error('Enter a comp percent between 0 and 5.');
      return api<{ ok: boolean; lpCompPct: number }>(`/api/lender-sheets/${lenderKey}/comp`, { method: 'PUT', body: JSON.stringify({ lpCompPct: n }) });
    },
    onSuccess: (r) => {
      setMsg({ tone: 'ok', text: `Lender-paid comp set to ${r.lpCompPct.toFixed(3)}% — LP scenarios price net of it from the next quote.` });
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Broker compensation</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Lender-paid comp</span>
        <input
          type="number"
          step="0.125"
          min={0}
          max={5}
          value={pct}
          disabled={!canEdit}
          onChange={(e) => setPct(e.target.value)}
          className="w-24 rounded-md border border-slate-300 px-2 py-1 text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
        />
        <span className="text-slate-500">%</span>
        <Button size="sm" variant="secondary" disabled={!canEdit || save.isPending || current == null} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save comp'}
        </Button>
        <span className="text-[11px] text-slate-400">Netted out of every price when a scenario is priced as LP; BP scenarios show the gross sheet price.</span>
      </div>
      {msg && <ResultLine tone={msg.tone} text={msg.text} />}
    </div>
  );
}

// PennyMac TPO — the POWER+ portal has no public rate-sheet listing. With a saved
// POWER+ login the worker signs in every hour (headless browser) and imports the
// newest broker sheet; the manual upload stays as a fallback. The workbook's "Price"
// tab carries the rates; a download without them is stored but never priced.
function PennymacPanel({ canEdit, existingConfig }: { canEdit: boolean; existingConfig: { username: string; totpConfigured: boolean } | null }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [loginMsg, setLoginMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (existingConfig) setUsername(existingConfig.username ?? '');
  }, [existingConfig]);
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const pm = status.data?.lenders.find((l) => l.key === 'pennymac');
  const saveLogin = useMutation({
    mutationFn: () => {
      if (!username.trim() || !password) throw new Error('Enter the POWER+ user name and password.');
      return api<{ ok: boolean; totpConfigured: boolean }>('/api/integrations/pennymac-power', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password, ...(totpSecret.trim() ? { totpSecret: totpSecret.trim() } : {}) }),
      });
    },
    onSuccess: (r) => {
      setLoginMsg({
        tone: 'ok',
        text: `Saved. The password${r.totpConfigured ? ' and authenticator key are' : ' is'} stored encrypted and never shown again.${r.totpConfigured ? '' : ' Without an authenticator key the pull will stop at PennyMac’s 6-digit code prompt.'} The worker pulls the sheet every hour from now on.`,
      });
      setPassword('');
      setTotpSecret('');
      void qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  const testPull = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; publishedAt: string; versionKey?: string; priceRows?: number; hasPrices?: boolean } | null }>(
        '/api/integrations/pennymac-power/test',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      setLoginMsg(
        r.ok
          ? {
              tone: 'ok',
              text:
                r.detail?.status === 'downloaded'
                  ? `Signed in and pulled the sheet published ${r.detail.publishedAt} — ${r.detail.priceRows?.toLocaleString() ?? '?'} price rows${r.detail.hasPrices ? ', now current' : ' (no rates in it)'}.`
                  : `Signed in — the portal's newest sheet (${r.detail?.publishedAt ?? '?'}) is already stored.`,
            }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
      void qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose the PennyMac_Ratesheet.xlsx file first.');
      return api<{ ok: boolean; hasPrices: boolean; priceRows: number; productCount: number; effectiveDateTime: string | null; isCurrent: boolean; notes: string[] }>(
        `/api/lender-sheets/upload?lender=pennymac&fileName=${encodeURIComponent(file.name)}`,
        { method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' } },
      );
    },
    onSuccess: (r) => {
      setMsg(
        r.hasPrices
          ? { tone: 'ok', text: `Stored sheet ${r.effectiveDateTime ?? ''} — ${r.priceRows.toLocaleString()} price rows across ${r.productCount} products${r.isCurrent ? ' · now current' : ' · an newer priced sheet stays current'}.` }
          : { tone: 'err', text: `Stored sheet ${r.effectiveDateTime ?? ''} but it has NO rates (the Price tab is empty — a template download). ${r.productCount} products and every LLPA table were read. Download it again from POWER+ during pricing hours and re-upload.` },
      );
      setFile(null);
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  return (
    <Card>
      <CardHeader
        title="PennyMac TPO — POWER+ rate sheets"
        subtitle="Save the POWER+ login and the worker signs in every hour (headless browser) to pull the newest Best Effort broker sheet; Mana Pricing takes its PennyMac fees and loan limits from it (rates are priced live on POWER+). Manual upload stays available below."
        actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>}
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="POWER+ user name">
            <Input value={username} onChange={(e) => setUsername(e.target.value)} disabled={!canEdit} placeholder="name@manahomeloans.com" />
          </Field>
          <Field label="Password" hint="Stored encrypted server-side. Never returned to the browser — re-enter to change.">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={!canEdit} />
          </Field>
          <div className="md:col-span-2">
            <Field
              label={`Authenticator key (TOTP)${existingConfig?.totpConfigured ? ' — saved' : ''}`}
              hint="PennyMac’s sign-in asks a new browser for a 6-digit Google Authenticator code. In PennyMac’s MFA setup choose the authenticator app, pick “enter the key manually / trouble scanning”, and paste that base32 key here (add the same key to your phone’s app so both keep working). Stored encrypted; leave blank to keep the saved key."
            >
              <Input type="password" value={totpSecret} onChange={(e) => setTotpSecret(e.target.value)} disabled={!canEdit} placeholder="e.g. JBSW Y3DP EHPK 3PXP …" autoComplete="off" />
            </Field>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={!canEdit || saveLogin.isPending} onClick={() => saveLogin.mutate()}>
            {saveLogin.isPending ? 'Saving…' : 'Save POWER+ login'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || testPull.isPending || !existingConfig} onClick={() => testPull.mutate()} title="Runs the full sign-in and pull now (about a minute)">
            {testPull.isPending ? 'Signing in and pulling… (up to 2 min)' : 'Test sign-in & pull now'}
          </Button>
        </div>
        {loginMsg && <ResultLine tone={loginMsg.tone} text={loginMsg.text} />}
        <p className="mb-1.5 mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Manual upload</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={!canEdit}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-navy-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-navy-700 hover:file:bg-brand-navy-100"
          />
          <Button disabled={!canEdit || !file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : 'Upload rate sheet'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
        <LenderCompControl lenderKey="pennymac" canEdit={canEdit} />
        <div className="mt-5">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Stored sheets</p>
          {!pm || pm.versions.length === 0 ? (
            <p className="text-sm text-slate-500">No PennyMac sheet uploaded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1 pr-3">Effective</th>
                  <th className="py-1 pr-3">File</th>
                  <th className="py-1 pr-3">Products</th>
                  <th className="py-1 pr-3">Rates</th>
                  <th className="py-1 pr-3">Uploaded</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {pm.versions.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-800">{v.sheetDateTime ?? v.versionKey}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.sourceFileName}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.productCount}</td>
                    <td className="py-1.5 pr-3">{v.hasPrices ? <Badge tone="success">priced</Badge> : <Badge tone="warning">template · no rates</Badge>}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{fmtCT(v.fetchedAt)}</td>
                    <td className="py-1.5">{v.isCurrent && <Badge tone="info">current</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// Sun West (SWMC) — with a saved SunSoft login (the one swmc.com/tpo → "Rate Sheets" asks
// for) the worker downloads the wholesale rate sheet every hour and stores it only when the
// pricing changed. Manual upload stays available. The newest sheet by its printed date and
// time becomes current and the module prices Conventional on it.
function SunwestPanel({ canEdit, existingConfig }: { canEdit: boolean; existingConfig: SunwestSunsoftConfigPublic | null }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [loginMsg, setLoginMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (!existingConfig) return;
    setUsername(existingConfig.username ?? '');
    setCompanyCode(existingConfig.companyCode ?? '');
  }, [existingConfig]);
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const sw = status.data?.lenders.find((l) => l.key === 'sunwest');
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
    void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    void qc.invalidateQueries({ queryKey: ['integrations-status'] });
  };
  const saveLogin = useMutation({
    mutationFn: () => {
      if (!username.trim() || !password) throw new Error('Enter the SunSoft User ID and password.');
      return api<{ ok: boolean }>('/api/integrations/sunwest-sunsoft', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password, ...(companyCode.trim() ? { companyCode: companyCode.trim() } : {}) }),
      });
    },
    onSuccess: () => {
      setLoginMsg({ tone: 'ok', text: 'Saved. The password is stored encrypted and never shown again. Click “Test sign-in & pull now” to check it — the worker downloads the sheet every hour from now on.' });
      setPassword('');
      refreshAll();
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  const testPull = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; sheetDateTime: string | null; versionKey: string; productCount?: number; convGrids?: number; isCurrent?: boolean } | null }>(
        '/api/integrations/sunwest-sunsoft/test',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      const when = r.detail?.sheetDateTime ?? 'undated';
      setLoginMsg(
        r.ok
          ? {
              tone: 'ok',
              text:
                r.detail?.status === 'downloaded'
                  ? `Signed in and pulled the ${when} sheet — ${r.detail.productCount ?? '?'} rate grids (${r.detail.convGrids ?? '?'} conventional)${r.detail.isCurrent ? ' · now current' : ' · a newer sheet stays current'}.`
                  : `Signed in — Sun West’s current sheet (${when}) is already stored.`,
            }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      refreshAll();
    },
    onError: (e: Error) => setLoginMsg({ tone: 'err', text: e.message }),
  });
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose the SunWest_RateSheet_….xlsx file first.');
      return api<{ ok: boolean; sheetDate: string | null; sheetTime: string | null; productCount: number; convGrids: number; isCurrent: boolean; replacedExisting: boolean; notes: string[] }>(
        `/api/lender-sheets/upload?lender=sunwest&fileName=${encodeURIComponent(file.name)}`,
        { method: 'POST', body: file, headers: { 'Content-Type': 'application/octet-stream' } },
      );
    },
    onSuccess: (r) => {
      const when = r.sheetDate ? `${r.sheetDate}${r.sheetTime ? ` ${r.sheetTime} PT` : ''}` : 'undated';
      setMsg({
        tone: r.convGrids > 0 ? 'ok' : 'err',
        text:
          r.convGrids > 0
            ? `Stored the ${when} sheet — ${r.productCount} rate grids (${r.convGrids} conventional)${r.isCurrent ? ' · now current' : ' · a newer sheet stays current'}${r.replacedExisting ? ' · replaced the same version' : ''}.`
            : `Stored the ${when} sheet but found no conventional rate grids in it (${r.productCount} grids read). Check that this is the SWMC wholesale RATESHEET workbook.`,
      });
      setFile(null);
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const runs = (sw?.runs ?? []).slice(0, 5);
  // The rates Sun West's public pricing engine offers beyond the printed sheet (built once per sheet version; no login needed).
  const swCurrent = sw?.current[0] ?? null;
  const ext = swCurrent?.extension ?? null;
  const extend = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; versionKey?: string; added?: number; gridsExtended?: number; gridsSkipped?: number; scenarios?: number; notes?: string[] } }>(
        '/api/integrations/sunwest-sunsoft/extend',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      const d = r.detail;
      setMsg(
        r.ok && d
          ? { tone: 'ok', text: `Sun West's engine priced ${d.scenarios ?? '?'} scenarios — ${d.added ?? 0} extra rate${d.added === 1 ? '' : 's'} on ${d.gridsExtended ?? 0} grids${d.gridsSkipped ? ` (${d.gridsSkipped} grids skipped: their printed rates did not match the engine)` : ''}. They price in Mana Pricing and Other Lenders right away.` }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
      void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  return (
    <Card>
      <CardHeader
        title="Sun West Mortgage — SWMC wholesale rate sheet"
        subtitle="Save the SunSoft login (the one swmc.com/tpo → Rate Sheets asks for) and the worker downloads the SWMC Wholesale rate sheet every hour, keeping a new version only when the pricing changed. The Other Lenders and Mana Pricing modules price Conventional (conforming, high balance, HomeReady, Home Possible, HomeOne) on the newest sheet. Manual upload stays available below."
        actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>}
      />
      <CardBody>
        {existingConfig?.signInRefusedAt && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Hourly pull paused — SunSoft refused the saved login twice in a row (last on {fmtCT(existingConfig.signInRefusedAt)}). Re-save the User ID and password, or run the test, to resume. Pausing keeps repeated failed sign-ins from locking the SunSoft account.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="SunSoft User ID">
            <Input value={username} onChange={(e) => setUsername(e.target.value)} disabled={!canEdit} autoComplete="off" />
          </Field>
          <Field label="SunSoft password" hint="Stored encrypted server-side. Never returned to the browser — re-enter to change.">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={!canEdit} autoComplete="new-password" />
          </Field>
          <Field label="Company code (optional)" hint="Leave blank for Mana’s own SunSoft login. Only a Sun West staff login needs it — use the Client Code printed on the rate sheet.">
            <Input value={companyCode} onChange={(e) => setCompanyCode(e.target.value.toUpperCase())} disabled={!canEdit} autoComplete="off" />
          </Field>
          <p className="self-end pb-1 text-[11px] text-slate-500">
            No SunSoft login yet? Sun West’s Client Relations Manager issues one: (844) 678-6837.
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={!canEdit || saveLogin.isPending} onClick={() => saveLogin.mutate()}>
            {saveLogin.isPending ? 'Saving…' : 'Save SunSoft login'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || testPull.isPending || !existingConfig} onClick={() => testPull.mutate()} title="Signs in and downloads the rate sheet now (a few seconds)">
            {testPull.isPending ? 'Signing in and downloading…' : 'Test sign-in & pull now'}
          </Button>
        </div>
        {loginMsg && <ResultLine tone={loginMsg.tone} text={loginMsg.text} />}
        {runs.length > 0 && (
          <div className="mt-5">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Recent pulls</p>
            <ul className="space-y-1 text-xs">
              {runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-baseline gap-2">
                  <span className="tabular-nums text-slate-500">{fmtCT(r.startedAt)}</span>
                  <span className="text-slate-400">{r.trigger === 'cron' ? 'hourly' : 'manual'}</span>
                  {r.status === 'ok' ? <Badge tone="success">{r.downloaded ? 'new sheet' : 'no change'}</Badge> : r.status === 'error' ? <Badge tone="danger">failed</Badge> : <Badge tone="neutral">running</Badge>}
                  {r.message && <span className="text-slate-600">{r.message}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mb-1.5 mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Manual upload</p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={!canEdit}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-navy-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-navy-700 hover:file:bg-brand-navy-100"
          />
          <Button disabled={!canEdit || !file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : 'Upload rate sheet'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
        <p className="mb-1.5 mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Extra rates from Sun West’s pricing engine</p>
        <p className="text-xs text-slate-600">
          {!swCurrent
            ? 'No Sun West sheet loaded yet.'
            : ext
              ? `Sheet ${fmtSheetStamp(swCurrent)} · ${ext.added} rate${ext.added === 1 ? '' : 's'} beyond the printed sheet on ${ext.gridsExtended} grids${ext.gridsSkipped ? ` · ${ext.gridsSkipped} grids skipped (printed rates did not match the engine)` : ''} · pulled ${fmtCT(ext.fetchedAt)}`
              : `Not built yet for sheet ${fmtSheetStamp(swCurrent)} — it runs with the next hourly check, or click the button.`}
        </p>
        {ext && ext.notes.length > 0 && (
          <details className="mt-1 text-[11px] text-slate-500">
            <summary className="cursor-pointer">Notes ({ext.notes.length})</summary>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {ext.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </details>
        )}
        <div className="mt-2">
          <Button variant="secondary" disabled={!canEdit || extend.isPending || !swCurrent} onClick={() => extend.mutate()} title="Prices a few canonical scenarios on Sun West's public engine (about a minute) and stores the rates its ladders carry beyond the printed sheet">
            {extend.isPending ? 'Pricing on Sun West’s engine…' : 'Pull the extra rates from Sun West’s engine now'}
          </Button>
        </div>
        <LenderCompControl lenderKey="sunwest" canEdit={canEdit} />
        <div className="mt-5">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Stored sheets</p>
          {!sw || sw.versions.length === 0 ? (
            <p className="text-sm text-slate-500">No Sun West sheet stored yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1 pr-3">Sheet date</th>
                  <th className="py-1 pr-3">File</th>
                  <th className="py-1 pr-3">Rate grids</th>
                  <th className="py-1 pr-3">Stored</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {sw.versions.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-800">{v.sheetDateTime ?? v.versionKey}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.sourceFileName}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{v.productCount}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{fmtCT(v.fetchedAt)}</td>
                    <td className="py-1.5">{v.isCurrent && <Badge tone="info">current</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// First Colony (FCM TPO) CODA portal login — used by the hourly rate-sheet pull only if
// the portal starts requiring a login for its rate-sheet listing (today it is public).
function FcmCodaPanel({
  canEdit,
  existingConfig,
}: {
  canEdit: boolean;
  existingConfig: { username: string; tierKeys: string[] } | null;
}) {
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (existingConfig) setUsername(existingConfig.username ?? '');
  }, [existingConfig]);

  const save = useMutation({
    mutationFn: () => {
      if (!username.trim() || !password) throw new Error('Enter the CODA username (email) and password.');
      return api('/api/integrations/fcm-coda', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), password }),
      });
    },
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Saved. The password is stored encrypted and never shown again.' });
      setPassword('');
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { user: string | null; files: number } | null }>(
        '/api/integrations/fcm-coda/test',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) =>
      setMsg(
        r.ok
          ? { tone: 'ok', text: `Signed in${r.detail?.user ? ` as ${r.detail.user}` : ''} — ${r.detail?.files ?? 0} rate-sheet files listed today.` }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      ),
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const refresh = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; downloaded: string[]; errors: string[]; checked: number }>('/api/lender-sheets/refresh', {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: (r) => {
      setMsg(
        r.errors.length
          ? { tone: 'err', text: r.errors.join(' | ') }
          : { tone: 'ok', text: r.downloaded.length ? `Pulled ${r.downloaded.join(', ')}` : `Up to date — no new sheet version (${r.checked} listed).` },
      );
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  // The rates CODA's engine offers beyond the printed sheet (built once per sheet version when the login is saved).
  const status = useQuery<LenderSheetStatus>({ queryKey: ['lender-sheets-status'], queryFn: () => api('/api/lender-sheets') });
  const fcmCurrent = status.data?.lenders.find((l) => l.key === 'fcm')?.current.find((c) => c.family === 'qm') ?? null;
  const ext = fcmCurrent?.extension ?? null;
  const extend = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null; detail?: { status: string; versionKey?: string; added?: number; gridsExtended?: number; gridsSkipped?: number; scenarios?: number; notes?: string[] } }>(
        '/api/integrations/fcm-coda/extend',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) => {
      const d = r.detail;
      setMsg(
        r.ok && d
          ? { tone: 'ok', text: `CODA priced ${d.scenarios ?? '?'} scenarios — ${d.added ?? 0} extra rate${d.added === 1 ? '' : 's'} on ${d.gridsExtended ?? 0} grids${d.gridsSkipped ? ` (${d.gridsSkipped} grids skipped: their printed rates did not match CODA)` : ''}. They price in Mana Pricing and Other Lenders right away.` }
          : { tone: 'err', text: r.errorMessage ?? 'Failed' },
      );
      void qc.invalidateQueries({ queryKey: ['lender-sheets-status'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
      void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="First Colony Wholesale — CODA rate sheets"
        subtitle="Rate sheets are pulled from coda.firstcolony.com every hour (new versions only) and price the Other Lenders and Mana Pricing modules. With the CODA login saved, each new sheet is also extended with the rates CODA's pricing engine offers beyond the 25 rates printed per grid (checked against the printed rates first)."
        actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>}
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="CODA username (email)">
            <Input value={username} onChange={(e) => setUsername(e.target.value)} disabled={!canEdit} placeholder="name@manahomeloans.com" />
          </Field>
          <Field label="Password" hint="Stored encrypted server-side. Never returned to the browser — re-enter to change.">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={!canEdit} />
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button disabled={!canEdit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save CODA login'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || test.isPending || !existingConfig} onClick={() => test.mutate()}>
            {test.isPending ? 'Testing…' : 'Test sign-in'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Pulling…' : 'Pull rate sheets now'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || extend.isPending || !existingConfig} onClick={() => extend.mutate()} title="Prices CODA's canonical scenarios and stores the rates its engine offers beyond the printed sheet (about 20 pricing calls)">
            {extend.isPending ? 'Pricing in CODA…' : 'Pull the extra rates from CODA now'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
        <p className="mt-3 text-xs text-slate-600">
          <span className="font-semibold uppercase tracking-wide text-slate-500">Extra rates from CODA: </span>
          {!fcmCurrent
            ? 'no conventional sheet loaded yet.'
            : ext
              ? `sheet ${fmtSheetStamp(fcmCurrent)} · ${ext.added} rate${ext.added === 1 ? '' : 's'} beyond the printed sheet on ${ext.gridsExtended} grids${ext.gridsSkipped ? ` · ${ext.gridsSkipped} grids skipped (printed rates did not match CODA)` : ''} · pulled ${fmtCT(ext.fetchedAt)}`
              : existingConfig
                ? `not built yet for sheet ${fmtSheetStamp(fcmCurrent)} — it runs with the next hourly check, or click the button.`
                : 'save the CODA login to enable them.'}
        </p>
        {ext && ext.notes.length > 0 && (
          <details className="mt-1 text-[11px] text-slate-500">
            <summary className="cursor-pointer">Notes from the last pull ({ext.notes.length})</summary>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {ext.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </details>
        )}
        <LenderCompControl lenderKey="fcm" canEdit={canEdit} />
      </CardBody>
    </Card>
  );
}

function StatusPill({ entry }: { entry?: StatusEntry }) {
  if (!entry) return null;
  if (!entry.configured) return null;
  const ok = entry.lastTestStatus !== 'failure';
  return (
    <span className="ml-2 inline-flex items-center gap-1">
      <StatusDot tone={ok ? 'success' : 'danger'} />
      <span className="text-[10px] uppercase tracking-wider text-slate-500">
        {ok ? 'OK' : 'Error'}
      </span>
    </span>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        '-mb-px flex items-center border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
        active
          ? 'border-brand-navy-700 text-brand-navy-800'
          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

function GmailPanel() {
  const qc = useQueryClient();
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [port, setPort] = useState(587);
  const [secure, setSecure] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api('/api/integrations/gmail-smtp', {
        method: 'POST',
        body: JSON.stringify({ fromEmail, fromName, appPassword, host: 'smtp.gmail.com', port, secure }),
      }),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Saved' });
      setAppPassword('');
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const test = useMutation({
    mutationFn: () => api('/api/integrations/gmail-smtp/test', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => setMsg({ tone: 'ok', text: 'Test email sent — check your inbox.' }),
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="Gmail SMTP"
        subtitle="Per-MLO. Use a Google App Password (requires 2-Step Verification on the Google account)."
        actions={<Badge tone="info">smtp.gmail.com</Badge>}
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="From email">
            <Input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="you@gmail.com"
            />
          </Field>
          <Field label="From name">
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Your Name, Mana Home Loans" />
          </Field>
          <Field label="App password" hint="Never returned to the browser after save.">
            <Input
              type="password"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              placeholder="16-character app password"
            />
          </Field>
          <Field label="Port">
            <Select value={port} onChange={(e) => setPort(Number(e.target.value))}>
              <option value={587}>587 (STARTTLS)</option>
              <option value={465}>465 (SSL)</option>
            </Select>
          </Field>
          <Field label="Secure mode">
            <Select value={secure ? 'yes' : 'no'} onChange={(e) => setSecure(e.target.value === 'yes')}>
              <option value="no">STARTTLS (port 587)</option>
              <option value="yes">SSL (port 465)</option>
            </Select>
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save SMTP settings'}
          </Button>
          <Button variant="secondary" onClick={() => test.mutate()} disabled={test.isPending}>
            {test.isPending ? 'Sending…' : 'Send test email'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
      </CardBody>
    </Card>
  );
}

/**
 * SendGrid (per-MLO, like Gmail SMTP): an API key with the Mail Send permission and a From address
 * that is a verified sender in SendGrid. The Mana Pricing email cards offer "Via: SendGrid" once
 * it is saved; the key never returns to the browser, and a later save without a key keeps it.
 */
function SendGridPanel({ existing }: { existing: SendGridConfigPublic | null }) {
  const qc = useQueryClient();
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  useEffect(() => {
    if (!existing) return;
    setFromEmail(existing.fromEmail);
    setFromName(existing.fromName);
    setReplyTo(existing.replyTo ?? '');
  }, [existing]);

  const save = useMutation({
    mutationFn: () =>
      api('/api/integrations/sendgrid', {
        method: 'POST',
        body: JSON.stringify({ fromEmail, fromName, replyTo: replyTo.trim() || null, apiKey: apiKey.trim() }),
      }),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Saved — send a test email to confirm the key and the From address.' });
      setApiKey('');
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const test = useMutation({
    mutationFn: () => api('/api/integrations/sendgrid/test', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Test email sent through SendGrid — check your inbox.' });
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="SendGrid"
        subtitle="Per-MLO. An API key with the Mail Send permission; the From address (or its domain) must be a verified sender in SendGrid. Pick “Via: SendGrid” on a Mana Pricing email card to send through it."
        actions={<Badge tone="info">api.sendgrid.com</Badge>}
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="From email" hint="Verified in SendGrid under Settings → Sender Authentication.">
            <Input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="you@manahomeloans.com" autoComplete="off" />
          </Field>
          <Field label="From name">
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Your Name, Mana Home Loans" />
          </Field>
          <Field label="Reply-to (optional)" hint="Replies go here instead of the From address.">
            <Input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="you@gmail.com" autoComplete="off" />
          </Field>
          <Field label="API key" hint={existing ? 'Saved and encrypted — leave blank to keep it.' : 'Never returned to the browser after save.'}>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="SG.…" autoComplete="off" />
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !fromEmail || !fromName || (!existing && !apiKey.trim())}>
            {save.isPending ? 'Saving…' : 'Save SendGrid settings'}
          </Button>
          <Button variant="secondary" onClick={() => test.mutate()} disabled={test.isPending || !existing}>
            {test.isPending ? 'Sending…' : 'Send test email'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
      </CardBody>
    </Card>
  );
}

/**
 * Outbound WhatsApp is locked to Twilio (BSP). The Meta Cloud API direct
 * path is deliberately not exposed here — the saved-config wire format still
 * accepts cloud_api for legacy reads, but the API rejects new cloud_api
 * saves and the worker treats any stale cloud_api row as manual (no sends).
 */
type WhatsAppPanelMode = 'manual' | 'twilio';

function WhatsAppPanel({
  canEdit,
  existingConfig,
}: {
  canEdit: boolean;
  existingConfig: WhatsAppConfigPublic | null;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<WhatsAppPanelMode>('manual');
  // Twilio fields
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioFromNumber, setTwilioFromNumber] = useState('');
  const [twilioMessagingServiceSid, setTwilioMessagingServiceSid] = useState('');
  const [twilioContentSid, setTwilioContentSid] = useState('');

  // Pre-populate the form from whatever's currently persisted. Without this,
  // every page visit starts with the +14155238886 sandbox placeholder in the
  // From field — and a Save click silently overwrites a properly-configured
  // production setup with sandbox routing. Auth token is never returned by
  // the server (security), so the user must re-paste it on every change.
  useEffect(() => {
    if (!existingConfig) return;
    setMode(existingConfig.mode === 'twilio' ? 'twilio' : 'manual');
    setTwilioAccountSid(existingConfig.twilioAccountSid ?? '');
    setTwilioFromNumber(existingConfig.twilioFromNumber ?? '');
    setTwilioMessagingServiceSid(existingConfig.twilioMessagingServiceSid ?? '');
    setTwilioContentSid(existingConfig.twilioDefaultContentSid ?? '');
  }, [existingConfig]);

  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () => {
      // Twilio mode requires the Auth Token, and the server never returns it
      // (security) — so this field is blank on every reopen and must be
      // re-pasted on each save. Guard here so a blank token shows a clear
      // message instead of a request that hangs (the API has no async-error
      // wrapper, so a server-side validation throw never sends a response).
      if (mode === 'twilio' && twilioAuthToken.trim().length < 10) {
        throw new Error(
          'Re-enter your Twilio Auth Token to save — it is never stored in the browser, so you must paste it again on every change to the WhatsApp config (including just adding the Content SID).',
        );
      }
      const body: Record<string, unknown> =
        mode === 'manual'
          ? { mode: 'manual' }
          : {
              mode: 'twilio',
              accountSid: twilioAccountSid,
              authToken: twilioAuthToken,
              // Send fromNumber only when the user filled it. The server
              // validates that at least one of fromNumber / messagingServiceSid
              // is present; Twilio will reject any request that includes both
              // From and MessagingServiceSid, so we let the messaging service
              // take precedence when set.
              ...(twilioFromNumber && !twilioMessagingServiceSid
                ? { fromNumber: twilioFromNumber }
                : {}),
              ...(twilioMessagingServiceSid
                ? { messagingServiceSid: twilioMessagingServiceSid }
                : {}),
              ...(twilioContentSid ? { defaultContentSid: twilioContentSid } : {}),
            };
      return api<{
        ok: boolean;
        config: WhatsAppConfigPublic | null;
      }>('/api/integrations/whatsapp', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (r) => {
      // Build a precise success message from the post-save config so the
      // user knows exactly which routing path is now active. Catches the
      // "I saved but my SID didn't take" failure mode by reflecting reality.
      let detail = 'Saved.';
      if (r.config) {
        if (r.config.twilioMessagingServiceSid) {
          detail = `Saved · routing via Messaging Service ${r.config.twilioMessagingServiceSid}`;
        } else if (r.config.twilioFromNumber) {
          const isSandbox = r.config.twilioFromNumber.includes('14155238886');
          detail = `Saved · routing via From=${r.config.twilioFromNumber}${
            isSandbox ? ' (SANDBOX)' : ''
          }`;
        } else if (r.config.mode === 'manual') {
          detail = 'Saved · manual (click-to-chat) mode';
        }
      }
      setMsg({ tone: 'ok', text: detail });
      setTwilioAuthToken('');
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; manualUrl?: string; error?: string }>('/api/integrations/whatsapp/test', {
        method: 'POST',
        body: JSON.stringify({ to: testTo, message: 'Mana test message' }),
      }),
    onSuccess: (r) =>
      setMsg(
        r.manualUrl
          ? { tone: 'ok', text: `Manual link: ${r.manualUrl}` }
          : r.ok
            ? { tone: 'ok', text: 'Sent' }
            : { tone: 'err', text: r.error ?? 'failed' },
      ),
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="WhatsApp"
        subtitle="Your own Twilio WhatsApp (Business Solution Provider) — used for your manual sends and your loans' automated notifications."
      />
      <CardBody>
        <Field label="Mode">
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as WhatsAppPanelMode)}
            disabled={!canEdit}
          >
            <option value="manual">Manual (click-to-chat URLs)</option>
            <option value="twilio">Twilio (BSP)</option>
          </Select>
        </Field>

        {existingConfig && (
          <div className="mt-3 rounded-lg border border-brand-navy-200 bg-brand-navy-50/40 px-3 py-2 text-xs text-brand-navy-800">
            <div className="font-semibold uppercase tracking-wider text-[10px] text-brand-navy-600 mb-1">
              Currently saved
            </div>
            <div>
              Mode: <code className="font-mono">{existingConfig.mode}</code>
              {existingConfig.twilioAccountSid && (
                <>
                  {' · '}Account SID:{' '}
                  <code className="font-mono">{existingConfig.twilioAccountSid}</code>
                </>
              )}
              {existingConfig.twilioMessagingServiceSid ? (
                <>
                  {' · '}Routing via Messaging Service:{' '}
                  <code className="font-mono">
                    {existingConfig.twilioMessagingServiceSid}
                  </code>
                </>
              ) : existingConfig.twilioFromNumber ? (
                <>
                  {' · '}From:{' '}
                  <code className="font-mono">{existingConfig.twilioFromNumber}</code>
                </>
              ) : null}
            </div>
            {existingConfig.twilioFromNumber === '+14155238886' &&
              !existingConfig.twilioMessagingServiceSid && (
                <div className="mt-1.5 rounded bg-rose-50 px-2 py-1 text-rose-800 ring-1 ring-rose-200">
                  ⚠ This config is routing through the Twilio Sandbox. To use your
                  paid sender, add the Messaging Service SID below and clear the From
                  Number, then re-paste your Auth Token and click Save.
                </div>
              )}
          </div>
        )}

        {mode === 'twilio' && (
          <>
            <div className="mt-4 rounded-lg border border-brand-gold-200 bg-brand-gold-50/40 px-3 py-2 text-xs text-slate-700">
              <strong>Twilio sandbox shortcut:</strong> use the shared From number{' '}
              <code className="font-mono text-brand-navy-700">+14155238886</code>. From any phone you want to receive test messages on, open WhatsApp and text{' '}
              <code className="font-mono">join &lt;your-code&gt;</code> to that number. Get your <em>join code</em> at{' '}
              <a
                href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn"
                target="_blank"
                rel="noreferrer"
                className="text-brand-navy-700 underline"
              >
                Twilio Console → Try it out → WhatsApp
              </a>
              . That bypasses the 24-hour window and all Meta business-verification friction for dev/demo.
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Account SID" hint="Starts with AC…">
                <Input
                  value={twilioAccountSid}
                  onChange={(e) => setTwilioAccountSid(e.target.value.trim())}
                  placeholder="AC1234567890abcdef1234567890abcdef"
                  disabled={!canEdit}
                />
              </Field>
              <Field
                label="From number (E.164)"
                hint={
                  twilioMessagingServiceSid
                    ? 'Ignored when a Messaging Service SID is set — Twilio routes via the service.'
                    : 'Direct sender. Sandbox: +14155238886. Production: your registered WhatsApp number.'
                }
              >
                <Input
                  value={twilioFromNumber}
                  onChange={(e) => setTwilioFromNumber(e.target.value.trim())}
                  placeholder="+13125550199"
                  disabled={!canEdit || !!twilioMessagingServiceSid}
                />
              </Field>
              <Field
                label="Messaging Service SID (recommended for production)"
                hint="MG… 34 chars. Twilio routes via the service's sender pool — set this and leave From Number blank for paid Twilio."
              >
                <Input
                  value={twilioMessagingServiceSid}
                  onChange={(e) => setTwilioMessagingServiceSid(e.target.value.trim())}
                  placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Auth token" hint="Stored encrypted; never returned to the browser.">
                <Input
                  type="password"
                  value={twilioAuthToken}
                  onChange={(e) => setTwilioAuthToken(e.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field
                label="Default Content SID (optional)"
                hint="HX… template used for sends outside the 24-hour window."
              >
                <Input
                  value={twilioContentSid}
                  onChange={(e) => setTwilioContentSid(e.target.value.trim())}
                  placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  disabled={!canEdit}
                />
              </Field>
            </div>
          </>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <Button disabled={!canEdit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save WhatsApp settings'}
          </Button>
        </div>

        <div className="mt-5 border-t border-slate-100 pt-5">
          <Field label="Test recipient (E.164 phone)">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="+15551112222"
              disabled={!canEdit}
            />
          </Field>
          <div className="mt-3">
            <Button variant="secondary" disabled={!canEdit || test.isPending} onClick={() => test.mutate()}>
              {test.isPending ? 'Working…' : 'Test send'}
            </Button>
          </div>
        </div>

        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
      </CardBody>
    </Card>
  );
}

function UwmPanel({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'mock' | 'real'>('mock');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tokenUrl, setTokenUrl] = useState(
    'https://login.microsoftonline.com/13d3189d-22a4-4f96-bbc9-2af46c222581/oauth2/v2.0/token',
  );
  const [scope, setScope] = useState('api://5260c3e4-e629-4b1c-b26d-3bb08407fc75/.default');
  const [endpoint, setEndpoint] = useState('https://api.uwm.com/brokers/insights/');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api('/api/integrations/uwm', {
        method: 'POST',
        body: JSON.stringify({ mode, clientId, clientSecret, tokenUrl, scope, apiEndpoint: endpoint }),
      }),
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Saved' });
      setClientSecret('');
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; tokenAcquired: boolean; testQueryOk: boolean; errorMessage?: string }>(
        '/api/integrations/uwm/test',
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (r) =>
      setMsg(r.ok ? { tone: 'ok', text: 'Token + test query OK' } : { tone: 'err', text: r.errorMessage ?? 'Failed' }),
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="UWM Insights API"
        subtitle="OAuth client credentials + GraphQL. Mock mode is the local-dev default."
        actions={
          canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>
        }
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Mode">
            <Select value={mode} onChange={(e) => setMode(e.target.value as 'mock' | 'real')} disabled={!canEdit}>
              <option value="mock">Mock (local development)</option>
              <option value="real">Real (production)</option>
            </Select>
          </Field>
          <Field label="API endpoint">
            <Input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Token URL">
            <Input value={tokenUrl} onChange={(e) => setTokenUrl(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Scope">
            <Input value={scope} onChange={(e) => setScope(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Client ID">
            <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Client Secret" hint="Stored encrypted. Never returned to the browser.">
            <Input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button disabled={!canEdit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save UWM settings'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || test.isPending} onClick={() => test.mutate()}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        </div>

        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
      </CardBody>
    </Card>
  );
}

function UwmPricingPanel({
  canEdit,
  existingConfig,
}: {
  canEdit: boolean;
  existingConfig: UwmPricingConfigPublic | null;
}) {
  const qc = useQueryClient();
  const [environment, setEnvironment] = useState<'staging' | 'production'>('staging');
  const [brokerAlias, setBrokerAlias] = useState('');
  const [username, setUsername] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (!existingConfig) return;
    setEnvironment(existingConfig.environment === 'production' ? 'production' : 'staging');
    setBrokerAlias(existingConfig.brokerAlias ?? '');
    setUsername(existingConfig.username ?? '');
  }, [existingConfig]);

  const save = useMutation({
    mutationFn: () => {
      if (!clientId.trim() || !clientSecret.trim()) {
        throw new Error('Enter both the client ID and client secret.');
      }
      if (username.trim() && !password.trim()) {
        throw new Error('Enter the password for this username (UWM user credential).');
      }
      return api('/api/integrations/uwm-pricing', {
        method: 'POST',
        body: JSON.stringify({
          environment,
          brokerAlias: brokerAlias.trim() || undefined,
          username: username.trim() || undefined,
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          password: password.trim() || undefined,
        }),
      });
    },
    onSuccess: () => {
      setMsg({ tone: 'ok', text: `Saved (${environment}).` });
      setClientSecret('');
      setPassword('');
      qc.invalidateQueries({ queryKey: ['integrations-status'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; errorMessage?: string | null }>('/api/integrations/uwm-pricing/test', {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: (r) =>
      setMsg(r.ok ? { tone: 'ok', text: 'Token OK — credentials are valid.' } : { tone: 'err', text: r.errorMessage ?? 'Failed' }),
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="UWM Pricing API"
        subtitle="Instant Price Quote (ADFS client credentials). Powers the Pricing module."
        actions={canEdit ? null : <Badge tone="warning">View-only · admin required</Badge>}
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Environment" hint="Staging on the dev app; Production on the live server.">
            <Select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value === 'production' ? 'production' : 'staging')}
              disabled={!canEdit}
            >
              <option value="staging">Staging</option>
              <option value="production">Production</option>
            </Select>
          </Field>
          <Field label="Broker alias (optional)" hint="e.g. MI0000 — attributed on every quote.">
            <Input value={brokerAlias} onChange={(e) => setBrokerAlias(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Client ID">
            <Input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              disabled={!canEdit}
              placeholder="https://…"
            />
          </Field>
          <Field label="Client Secret" hint="Stored encrypted. Never returned to the browser — re-enter to save.">
            <Input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field
            label="Username (optional)"
            hint="UWM EASE email. Set this for a user credential (password grant); leave blank for client-credentials."
          >
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={!canEdit}
              placeholder="name@manahomeloans.com"
            />
          </Field>
          <Field label="Password" hint="Required when a username is set. Stored encrypted — re-enter to save.">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button disabled={!canEdit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save pricing credentials'}
          </Button>
          <Button variant="secondary" disabled={!canEdit || test.isPending} onClick={() => test.mutate()}>
            {test.isPending ? 'Testing…' : 'Test connection'}
          </Button>
        </div>
        {msg && <ResultLine tone={msg.tone} text={msg.text} />}
      </CardBody>
    </Card>
  );
}

// Per-MLO comp + closing-cost editor. Drives the ★ best-rate criteria across
// Mana Pricing, borrower emails, and My CRM. Whole dollars; absence = $0.
function CompCostsPanel() {
  const qc = useQueryClient();
  const cfgQuery = useQuery<{
    categories: Record<PricingCompCategory, { comp: number; closingCosts: number }>;
  }>({
    queryKey: ['pricing-comp-config'],
    queryFn: () => api('/api/pricing/comp-config'),
  });
  const [draft, setDraft] = useState<Record<string, { comp: string; closingCosts: string }>>({});
  const [seeded, setSeeded] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (!cfgQuery.data || seeded) return;
    const d: Record<string, { comp: string; closingCosts: string }> = {};
    for (const cat of PRICING_COMP_CATEGORIES) {
      const v = cfgQuery.data.categories[cat];
      d[cat] = { comp: String(v?.comp ?? 0), closingCosts: String(v?.closingCosts ?? 0) };
    }
    setDraft(d);
    setSeeded(true);
  }, [cfgQuery.data, seeded]);

  const setField = (cat: string, key: 'comp' | 'closingCosts', val: string) => {
    setMsg(null);
    setDraft((prev) => ({
      ...prev,
      [cat]: { comp: '0', closingCosts: '0', ...prev[cat], [key]: val.replace(/[^\d]/g, '') },
    }));
  };

  const save = useMutation({
    mutationFn: () => {
      const categories: Record<string, { comp: number; closingCosts: number }> = {};
      for (const cat of PRICING_COMP_CATEGORIES) {
        categories[cat] = {
          comp: Math.max(0, Math.round(Number(draft[cat]?.comp) || 0)),
          closingCosts: Math.max(0, Math.round(Number(draft[cat]?.closingCosts) || 0)),
        };
      }
      return api('/api/pricing/comp-config', { method: 'PUT', body: JSON.stringify({ categories }) });
    },
    onSuccess: () => {
      setMsg({ tone: 'ok', text: 'Saved' });
      void qc.invalidateQueries({ queryKey: ['pricing-comp-config'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: e.message }),
  });

  return (
    <Card>
      <CardHeader
        title="Comp & Closing Costs"
        subtitle="Your MLO compensation and closing costs per loan type. These pick the best (★) rate in Mana Pricing, borrower emails, and My CRM. Whole dollars; blank = $0."
      />
      <CardBody>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2">Loan category</th>
                <th className="px-3 py-2">MLO Comp ($)</th>
                <th className="px-3 py-2">Closing Costs ($)</th>
              </tr>
            </thead>
            <tbody>
              {PRICING_COMP_CATEGORIES.map((cat) => (
                <tr key={cat} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-brand-navy-800">
                    {PRICING_COMP_CATEGORY_LABELS[cat]}
                  </td>
                  <td className="px-3 py-2">
                    <MoneyInput value={draft[cat]?.comp ?? ''} onChange={(v) => setField(cat, 'comp', v)} />
                  </td>
                  <td className="px-3 py-2">
                    <MoneyInput
                      value={draft[cat]?.closingCosts ?? ''}
                      onChange={(v) => setField(cat, 'closingCosts', v)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending || cfgQuery.isLoading}>
            {save.isPending ? 'Saving…' : 'Save comp & closing costs'}
          </Button>
          {msg && (
            <span className={msg.tone === 'ok' ? 'text-sm text-emerald-600' : 'text-sm text-rose-600'}>
              {msg.text}
            </span>
          )}
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
          ★ rule, every lender: borrower-paid comp{' '}
          <span className="font-mono">lender credit − (closing costs + non-LLPA buydown cost) ≥ your comp</span>; lender-paid comp (the price is
          already net of the comp) <span className="font-mono">lender credit ≥ closing costs + non-LLPA buydown cost</span>. If no rate qualifies,
          nothing is starred and borrower emails fall back to “contact your loan officer.” The DSCR row covers every DSCR purpose — purchase,
          rate/term and cash-out — while the agency rows star purchases and rate/term refinances only.
        </p>
      </CardBody>
    </Card>
  );
}

function MoneyInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center">
      <span className="mr-1 text-slate-400">$</span>
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className="w-28 rounded-md border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
      />
    </div>
  );
}

function ResultLine({ tone, text }: { tone: 'ok' | 'err'; text: string }) {
  return (
    <div
      className={clsx(
        'mt-4 rounded-lg border px-3 py-2 text-xs',
        tone === 'ok'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-rose-200 bg-rose-50 text-rose-700',
      )}
    >
      {text}
    </div>
  );
}
